/* eslint-disable react/prop-types */
import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
} from "@material-ui/core";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import moment from "moment";

const UsageSection = ({ usage }) => {
  if (!usage) return null;

  let items = [];
  try {
    items = typeof usage === "string" ? JSON.parse(usage) : usage;
  } catch {
    return (
      <Box style={{ marginTop: 16 }}>
        <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>Usage</Typography>
        <Box style={{ background: "#f9f9f9", borderRadius: 6, padding: "10px 14px", fontFamily: "monospace", fontSize: 13 }}>
          {String(usage)}
        </Box>
      </Box>
    );
  }

  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <Box style={{ marginTop: 20 }}>
      <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 10 }}>
        Usage
      </Typography>
      <Grid container spacing={2}>
        {items.map((item, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Box
              style={{
                background: "#f9f9f9",
                border: "1px solid #eee",
                borderRadius: 8,
                padding: "12px 16px",
              }}
            >
              <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <Typography style={{ fontWeight: 700, fontSize: 13, textTransform: "capitalize" }}>
                  {item.name || `Feature ${i + 1}`}
                </Typography>
                <Chip
                  label={item.isActive ? "Active" : "Inactive"}
                  size="small"
                  style={{
                    background: item.isActive ? "#e8f5e9" : "#f5f5f5",
                    color: item.isActive ? "#2e7d32" : "#616161",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              </Box>
              {item.description && (
                <Typography style={{ fontSize: 12, color: "#777", marginBottom: 8 }}>
                  {item.description}
                </Typography>
              )}
              <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                {item.isUnlimited ? (
                  <Chip label="Unlimited" size="small" style={{ background: "#e3f2fd", color: "#1565c0", fontSize: 11 }} />
                ) : (
                  item.limit !== undefined && (
                    <Chip label={`Limit: ${item.limit}`} size="small" variant="outlined" style={{ fontSize: 11 }} />
                  )
                )}
                {item.usedLimit !== undefined && (
                  <Chip label={`Used: ${item.usedLimit}`} size="small" variant="outlined" style={{ fontSize: 11 }} />
                )}
                {item.usedImageLimit !== undefined && (
                  <Chip label={`Images used: ${item.usedImageLimit}`} size="small" variant="outlined" style={{ fontSize: 11 }} />
                )}
                {item.usedQuotationLimit !== undefined && (
                  <Chip label={`Quotations: ${item.usedQuotationLimit}`} size="small" variant="outlined" style={{ fontSize: 11 }} />
                )}
                {item.usedBillLimit !== undefined && (
                  <Chip label={`Bills: ${item.usedBillLimit}`} size="small" variant="outlined" style={{ fontSize: 11 }} />
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const InfoRow = ({ label, value }) => (
  <Grid container style={{ marginBottom: 10 }}>
    <Grid item xs={4} sm={3}>
      <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555" }}>{label}</Typography>
    </Grid>
    <Grid item xs={8} sm={9}>
      <Typography style={{ fontSize: 14 }}>{value || "N/A"}</Typography>
    </Grid>
  </Grid>
);

const StatusChip = ({ status }) => {
  const colorMap = {
    ACTIVE:    { bg: "#e8f5e9", color: "#2e7d32" },
    INACTIVE:  { bg: "#f5f5f5", color: "#616161" },
    EXPIRED:   { bg: "#ffebee", color: "#c62828" },
    CANCELLED: { bg: "#fff3e0", color: "#e65100" },
    PENDING:   { bg: "#e3f2fd", color: "#1565c0" },
  };
  const style = colorMap[status] || { bg: "#f5f5f5", color: "#333" };
  return (
    <Chip
      label={status || "N/A"}
      size="small"
      style={{ background: style.bg, color: style.color, fontWeight: 600 }}
    />
  );
};

const ViewUserSubscription = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { subscription, userId } = state || {};

  if (!subscription) {
    return (
      <Container maxWidth={false} disableGutters style={{ margin: "0 2%" }}>
        <Typography>No subscription data found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters style={{ margin: "0 2%", minHeight: "70vh" }}>
      {/* Breadcrumb */}
      <Typography
        style={{ fontWeight: "bold", display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}
      >
        <Link to="/admin/user" style={{ color: "black", textDecoration: "none", marginRight: 4 }}>
          Users
        </Link>
        &gt;
        <span
          style={{ cursor: "pointer", marginLeft: 4, marginRight: 4 }}
          onClick={() => navigate("/admin/user/view-user", { state: { id: userId } })}
        >
          View User
        </span>
        &gt;
        <span style={{ fontWeight: "bold", marginLeft: 4 }}>Subscription Detail</span>
      </Typography>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
        variant="outlined"
        size="small"
      >
        Back
      </Button>

      {/* Detail Card */}
      <Box
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 24,
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header row */}
        <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Subscription Detail
          </Typography>
          <StatusChip status={subscription.status} />
          {subscription.isFree && (
            <Chip label="Free" size="small" style={{ background: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />
          )}
          {subscription.isCancelled && (
            <Chip label="Cancelled" size="small" style={{ background: "#fff3e0", color: "#e65100", fontWeight: 600 }} />
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoRow label="ID" value={subscription.id} />
            <InfoRow label="Subscription ID" value={subscription.subscriptionId} />
            <InfoRow label="Razorpay ID" value={subscription.razorpaySubscriptionId} />
            <InfoRow label="Status" value={subscription.status} />
            <InfoRow label="Free" value={subscription.isFree ? "Yes" : "No"} />
            <InfoRow label="Cancelled" value={subscription.isCancelled ? "Yes" : "No"} />
            <InfoRow label="Granted Via" value={subscription.grantedVia} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              label="Start Date"
              value={subscription.startDate ? moment(subscription.startDate).format("DD/MM/YYYY HH:mm") : null}
            />
            <InfoRow
              label="End Date"
              value={subscription.endDate ? moment(subscription.endDate).format("DD/MM/YYYY HH:mm") : null}
            />
            <InfoRow
              label="Created At"
              value={subscription.createdAt ? moment(subscription.createdAt).format("DD/MM/YYYY HH:mm") : null}
            />
            <InfoRow
              label="Updated At"
              value={subscription.updatedAt ? moment(subscription.updatedAt).format("DD/MM/YYYY HH:mm") : null}
            />
          </Grid>
        </Grid>

        {/* Usage */}
        <UsageSection usage={subscription.usage} />

        {/* Data */}
        {subscription.data && (
          <Box style={{ marginTop: 20 }}>
            <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 8 }}>
              Data
            </Typography>
            <Box
              style={{
                background: "#f9f9f9",
                borderRadius: 6,
                padding: "10px 14px",
                fontSize: 13,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                overflowX: "auto",
              }}
            >
              {typeof subscription.data === "object"
                ? JSON.stringify(subscription.data, null, 2)
                : String(subscription.data)}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ViewUserSubscription;
